import {
  ConvertToSeriesGameAsync,
  ConvertToSingleGameAsync,
  RebuildGameStoryAsync
} from '@maxpreps/apiserviceclients/clients/ContestsServiceClient';
import {
  displayToastMessage,
  ToastMode
} from '@maxpreps/shared/corecomponents/Toastify/Toast';
import {
  deleteBulkContests,
  restoreBulkContests
} from '@maxpreps/shared/data/ghost-apis/contests-api';
import { replace as routerReplace } from '@maxpreps/shared/functions/primitiveHelpers/routerHelpers';
import { AppType } from '@maxpreps/shared/functions/utils/requestContextUtils';
import getCSRRewriteAttributes from '../../utils/getCSRRewriteAttributes';
import { getTeamUrl } from './../../utils/navigation';
import { Page } from './../../utils/pages';

export const deleteBulkContestHelper = async (
  teamId,
  sportSeasonId,
  contestIds,
  token,
  contests,
  setContests,
  setSelectedContests
) => {
  const [data, error, json] = await deleteBulkContests(
    teamId,
    sportSeasonId,
    contestIds,
    token
  );

  if (json.status === 200) {
    const newContestData = contests.map(a => {
      const wasDeleted = data?.some(u => u.contestId === a.contestId);
      if (wasDeleted) {
        const contestCopy = { ...a };
        contestCopy.isDeleted = true;
        return contestCopy;
      }
      return a;
    });

    setContests(newContestData);
    setSelectedContests([]);
    displayToastMessage(
      `${data?.length} contest(s) deleted. The site will update in 15-30 minutes.`,
      ToastMode.Success
    );
  } else if (json.status === 206) {
    const deletedContests = data?.map(c => c.contestId);
    setContests(
      contests.map(c =>
        deletedContests?.includes(c.contestId) ? { ...c, isDeleted: true } : c
      )
    );
    displayToastMessage(
      `${deletedContests?.length} contest(s) deleted. Unable to delete ${
        contestIds.length - (deletedContests?.length ?? 0)
      } contest(s). The site will update in 15-30 minutes.`,
      ToastMode.Success
    );
  } else {
    console.error(error);
    displayToastMessage(`Cannot delete contests`, ToastMode.Error);
  }
};

export const restoreBulkContestHelper = async (
  teamId,
  sportSeasonId,
  contestIds,
  token,
  contests,
  setContests,
  setSelectedContests
) => {
  const [data, error] = await restoreBulkContests(
    teamId,
    sportSeasonId,
    contestIds,
    token
  );
  if (error) {
    console.error(error);
    displayToastMessage(
      (await (error as any).getResponseErrors()) ?? `Cannot restore contest(s)`,
      ToastMode.Error
    );
  } else {
    const newContestData = contests.map(a => {
      const wasRestored = data?.some(u => u.contestId === a.contestId);
      if (wasRestored) {
        const contestCopy = { ...a };
        contestCopy.isDeleted = false;
        return contestCopy;
      }
      return a;
    });

    setContests(newContestData);
    setSelectedContests([]);
    displayToastMessage(
      `${data?.length} contest(s) restored. The site will update in 15-30 minutes.`,
      ToastMode.Success
    );
  }
};

export const rebuildGameStoryHelper = async (
  contestId,
  teamId,
  sportSeasonId,
  token
) => {
  const [, error] = await RebuildGameStoryAsync(
    contestId,
    teamId,
    sportSeasonId,
    token
  );

  if (error) {
    displayToastMessage(`Unable to rebuild game story.`, ToastMode.Error);
  } else {
    console.error(error);
    displayToastMessage(
      `Game story rebuilding. The site will update within 5 hours.`,
      ToastMode.Success
    );
  }
};

export const convertToSeriesHelper = async (
  teamId,
  sportSeasonId,
  allSeasonId,
  contestId,
  teamCanonicalUrl,
  token
) => {
  const [, error] = await ConvertToSeriesGameAsync(
    teamId,
    sportSeasonId,
    contestId,
    token,
    {
      'mp-sources-page-source': 'Desktop_CoachAdmin',
      'mp-sources-contest-source': 'CoachAdmin',
      'mp-sources-contest-subsource': 'None',
      'mp-sources-contest-type': 'ContestUpdate',
      'mp-sources-contest-subtype': 'TournamentBind',
      'mp-sources-user-source': 'None'
    }
  );

  if (error) {
    displayToastMessage(`Unable to convert to seies.`, ToastMode.Error);
  } else {
    console.error(error);
    displayToastMessage(
      `Converted to series. The site will update in 15-30 minutes.`,
      ToastMode.Success
    );

    // CSR "Refresh" to get the updated schedule.
    const csrAttributes = getCSRRewriteAttributes(
      getTeamUrl(Page.Schedule, teamCanonicalUrl, { admin: 1 }),
      teamId,
      sportSeasonId,
      allSeasonId,
      AppType.None
    );
    routerReplace(csrAttributes.href, csrAttributes.as);
  }
};

export const convertToSingleGameHelper = async (
  teamId,
  sportSeasonId,
  allSeasonId,
  contestId,
  deleteContests,
  teamCanonicalUrl,
  token
) => {
  const [, error] = await ConvertToSingleGameAsync(
    teamId,
    sportSeasonId,
    contestId,
    deleteContests,
    token,
    {
      'mp-sources-page-source': 'Desktop_CoachAdmin',
      'mp-sources-contest-source': 'CoachAdmin',
      'mp-sources-contest-subsource': 'None',
      'mp-sources-contest-type': deleteContests
        ? 'ContestDeletion'
        : 'ContestUpdate',
      'mp-sources-contest-subtype': 'TournamentUnbind',
      'mp-sources-user-source': 'None'
    }
  );

  if (error) {
    displayToastMessage(`Unable to convert to single game.`, ToastMode.Error);
  } else {
    console.error(error);
    displayToastMessage(
      `Converted to single game. The site will update in 15-30 minutes.`,
      ToastMode.Success
    );

    // CSR "Refresh" to get the updated schedule.
    const csrAttributes = getCSRRewriteAttributes(
      getTeamUrl(Page.Schedule, teamCanonicalUrl, { admin: 1 }),
      teamId,
      sportSeasonId,
      allSeasonId,
      AppType.None
    );
    routerReplace(csrAttributes.href, csrAttributes.as);
  }
};
